import React, { useEffect, useRef, useState } from 'react';
import mime from 'mime';
import { Link } from '@mui/material';
import { styled } from 'theme';
import FileObj from 'domain/File';
import { FileUpload, FileUploadStatuses, getFilenameParts } from 'store/slices/file';
import { generateHeaders } from 'utils/APIUtils';
import axios, { AxiosResponse } from 'axios';
import { UploadFileAsLink } from './UploadFileAsLink';
import { UploadFileAsItem } from './UploadFileAsItem';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

const InputContainer = styled('div')({
    display: 'none',
});

const MainContainer = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    width: '100%',
});

export const UPLOADER_TYPES = {
    LINK: 'link',
    DROPZONE: 'dropzone',
} as const;

export const UPLOADER_CATEGORY = {
    PROFILE: 'profile',
    SPOT: 'spot',
} as const;

type UploaderType = typeof UPLOADER_TYPES[keyof typeof UPLOADER_TYPES];
type UploaderCategory = typeof UPLOADER_CATEGORY[keyof typeof UPLOADER_CATEGORY];

interface UploaderProps {
    fileTypes?: string[];
    type?: UploaderType;
    category: UploaderCategory;
    multiple?: boolean;
    onFileUploaded?: (_files: Partial<FileUpload>[]) => void;
    idUser: string;
    jwt: string;
    label?: string;
    files?: Partial<FileUpload>[];
}

interface NotifyFileUploadResponse {
    idFile: string;
    idUser?: string;
    fileName: string;
    fileType: string;
    bucket: string;
    url: string;
    deleted?: boolean;
    uploaded?: boolean;
    uploadDate?: string;
}

export const Uploader: React.FC<UploaderProps> = ({
    onFileUploaded,
    idUser,
    jwt,
    category,
    label,
    files: filesForPrepopulation,
    type = UPLOADER_TYPES.DROPZONE,
    fileTypes = [],
    multiple = true,
}) => {
    const theme = useTheme();
    const [files, setFiles] = useState<Partial<FileUpload>[]>(filesForPrepopulation ?? []);
    const [uploading, setUploading] = useState(false);
    const [, setError] = useState(false);

    const fileRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        const isUploading = files.some((file) =>
            [FileUploadStatuses.REQUESTED_UPLOAD,
                FileUploadStatuses.UPLOADING,
                FileUploadStatuses.UPLOAD_FINISHED].find((s) => s === file.status)?.length
        );
        setUploading(isUploading);

        const hasError = files.some((file) =>
            [FileUploadStatuses.REQUESTED_UPLOAD_FAIL,
                FileUploadStatuses.UPLOADING_FAIL,
                FileUploadStatuses.UPLOAD_FINISHED_FAIL].find((s) => s === file.status)?.length
        );
        setError(hasError);

        if (onFileUploaded) {
            onFileUploaded(files);
        }
    }, [files]);

    const deleteFile = async (file: Partial<FileUpload>): Promise<boolean> => {
        const headers = generateHeaders(jwt);
        setFiles((prevFiles) => prevFiles.map((f) =>
            f.idFile === file.idFile ? { ...f, status: FileUploadStatuses.DELETING } : f
        ));
        return axios.delete<void, boolean>(
            `${process.env.REACT_APP_API_URL}/file/${file.idFile}`,
            { headers }
        )
            .then((r) => {
                setFiles((prevFiles) => prevFiles.map((f) =>
                    f.idFile === file.idFile ? { ...f, status: FileUploadStatuses.DELETED } : f
                ));
                return r
            })
            .catch((error) => {
                setFiles((prevFiles) => prevFiles.map((f) =>
                    f.idFile === file.idFile ? { ...f, status: FileUploadStatuses.DELETE_FAILED } : f
                ));
                console.error('Error deleting files:', error);
                return false;
            });

    }

    const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const filesList = event.target.files;
        if (!filesList) return;

        const newFiles = Array.from(filesList).map((file) => ({
            file,
            fileName: `${Date.now()}-${file.name}`,
            fileType: mime.getType(file.name) || 'application/octet-stream',
            status: FileUploadStatuses.REQUESTED_UPLOAD,
        } as FileUpload));

        setFiles((prevFiles) => [...prevFiles, ...newFiles]);

        for (const fileUpload of newFiles) {
            const fileUploadCopy = { ...fileUpload }
            try {
                const uniqueSeed = Date.now();
                const headers = generateHeaders(jwt);
                const { fileName, fileType } = getFilenameParts(fileUploadCopy.file, idUser, uniqueSeed);

                const responseSignature = await axios.post<FileObj>(
                    `${process.env.REACT_APP_API_URL}/file`,
                    { fileName, fileType },
                    { headers }
                );
                fileUploadCopy.idFile = responseSignature.data.idFile;
                setFiles((prevFiles) => prevFiles.map((f) =>
                    f.fileName === fileUploadCopy.fileName ? {
                        ...f,
                        status: FileUploadStatuses.UPLOADING,
                        idFile: responseSignature.data.idFile,
                        fileName: responseSignature.data.fileName
                    } : f
                ));

                const { signedRequest, idFile } = responseSignature.data;

                await axios.put(signedRequest, fileUploadCopy.file, {
                    headers: {
                        'Content-Type': fileType,
                    },
                });

                setFiles((prevFiles) => prevFiles.map((f) =>
                    f.idFile === fileUploadCopy.idFile ? { ...f, status: FileUploadStatuses.UPLOAD_FINISHED } : f
                ));

                const responseFinishNotification: AxiosResponse<NotifyFileUploadResponse> = await axios.put(
                    `${process.env.REACT_APP_API_URL}/file/${idFile}`,
                    { category },
                    { headers }
                );
                setFiles((prevFiles) => prevFiles.map((f) =>
                    f.idFile === fileUploadCopy.idFile
                        ? {
                            ...f,
                            status: FileUploadStatuses.UPLOADED,
                            fileName: responseFinishNotification.data.fileName,
                            idFile: responseFinishNotification.data.idFile,
                            fileType: responseFinishNotification.data.fileType,
                            url: responseFinishNotification.data.url,
                        }
                        : f
                ));

            } catch (error) {
                console.log('error', error)
                setFiles((prevFiles) => prevFiles.map((f) => {
                    if (f.idFile === fileUploadCopy.idFile) {
                        if (fileUploadCopy.status === FileUploadStatuses.REQUESTED_UPLOAD) {
                            return { ...f, status: FileUploadStatuses.REQUESTED_UPLOAD_FAIL };
                        } else if (fileUploadCopy.status === FileUploadStatuses.UPLOADING) {
                            return { ...f, status: FileUploadStatuses.UPLOADING_FAIL };
                        } else if (fileUploadCopy.status === FileUploadStatuses.UPLOAD_FINISHED) {
                            return { ...f, status: FileUploadStatuses.UPLOAD_FINISHED_FAIL };
                        }
                    }
                    return f;
                }));
            }
        }
    };

    return (
        <>
            {type === UPLOADER_TYPES.LINK ? (
                <>
                    {files?.map((f) =>
                        <UploadFileAsLink file={f} key={`${f.fileName}-${f.idFile}`} label={label}/>
                    )}
                    {!files || !files.length ? <UploadFileAsLink file={null} label={label}/> : null}
                    {!uploading ?
                        <Box sx={{
                            pt: '.25em'
                        }}
                        >
                            <Link
                                color={'secondary'}
                                href='#'
                                underline='hover'
                                sx={{
                                    fontSize: '.9em'
                                }}
                                onClick={(e) => {
                                    if (!multiple) {
                                        void Promise.all(files?.map((f) => deleteFile(f)))
                                            .then(() => {
                                                setFiles([]);
                                            })
                                            .catch((error) => {
                                                setFiles([]);
                                                console.error('Error deleting files:', error);
                                            });
                                    }
                                    e.preventDefault();
                                    fileRef.current?.click();
                                }}
                            >
                                Change Profile<br/>Picture
                            </Link>
                        </Box> : null}
                </>) : null}
            {type === UPLOADER_TYPES.DROPZONE ? (
                <Box sx={{
                    textAlign: 'center',
                    padding: '2em 1em',
                    border: `dashed 3px ${theme.palette.primary.main}`,
                    marginBottom: '12px',
                    borderRadius: '15px',
                }}
                >
                    <Box
                        sx={{
                            mb: 3,
                        }}
                    >
                        <Link
                            href={'#'}
                            underline="hover"
                            onClick={() => {
                                fileRef.current?.click();
                            }}
                            sx={{
                                fontSize: '1.8em'
                            }}
                        >
                        Upload Pictures
                        </Link>
                    </Box>
                    <Typography variant={'body1'}>
                        (&nbsp;Click to Share Photos of the Spot&nbsp;)
                    </Typography>
                    <MainContainer>
                        {files?.map((f) =>
                            <UploadFileAsItem
                                onDeleteFile={() => void deleteFile(f)}
                                key={`${f.fileName}-${f.idFile}`}
                                file={f}
                            />
                        )}
                    </MainContainer>
                </Box>) : null}
            <InputContainer>
                <input
                    type='file'
                    accept={fileTypes.join(',')}
                    multiple={multiple}
                    ref={fileRef}
                    onChange={(event) => {
                        void handleUpload(event)
                    }}
                />
            </InputContainer>
        </>
    );
};

export default Uploader;
